// @flow

import React from 'react';

import HeaderLinks from 'web-ssr/app/common/components/ContentHeader/HeaderLinks';
import HeaderSearchToggle from 'web-ssr/app/common/components/ContentHeader/HeaderSearchToggle';
import OutsideClickable from 'web-ssr/app/common/components/OutsideClickable';
import { shouldEnableMobileMenu } from 'web-ssr/app/common/components/ContentHeader/utils';
import {
  HeaderBadge,
  HeaderBlock,
  HeaderBlockContainer,
  HeaderBlockHeaderLinks,
  HeaderBlockInnerWrapper,
  HeaderBlockLogo,
  HeaderBlockMenuControls,
  HeaderBlockOverlay,
  HeaderBlockServicesSearch,
  HeaderMenuButton,
  HeaderSignUpCta,
} from 'web-ssr/app/common/components/ContentHeader/HeaderBlock';

import type { Node } from 'react';

import type { HeaderLinkItemProps } from 'web-ssr/app/common/components/ContentHeader/HeaderLinks';
import type { HeaderMediaQueries } from 'web-ssr/app/common/components/ContentHeader/types';

export type HeaderProps = {
  actions: HeaderLinkItemProps[],
  badgeText?: string,
  currentUrl: string,
  forceRefreshOnExit: ?boolean,
  isFullWidth: boolean,
  /**
   * Adjusts the header to be compatible with 1200px design system layout.
   * This is a temporary fix until the new header is implemented.
   **/
  isDesignSystemHeader: boolean,
  isLoggedIn: boolean,
  isMenuVisible: boolean,
  isServiceSearchVisible: boolean,
  links: HeaderLinkItemProps[],
  matchesMediaQuery: HeaderMediaQueries,
  renderServicesSearch: ?() => Node,
  renderAccountActions: () => Node,
  onCloseServiceSearch: () => void,
  onOpenServiceSearch: () => void,
  onHideMenu: () => void,
  onMount: () => void,
  onToggleMenu: () => void,
};

const ContentHeader = (props: HeaderProps) => {
  const {
    actions,
    badgeText,
    currentUrl,
    forceRefreshOnExit,
    isFullWidth,
    isDesignSystemHeader,
    isLoggedIn,
    isMenuVisible,
    isServiceSearchVisible,
    links,
    onCloseServiceSearch,
    onHideMenu,
    onOpenServiceSearch,
    onToggleMenu,
    renderServicesSearch,
    renderAccountActions,
  } = props;
  const isMobileMenuEnabled = shouldEnableMobileMenu({
    isLoggedIn,
    mediaQueries: props.matchesMediaQuery,
  });

  const signUpCta = isLoggedIn ? null : (
    <HeaderSignUpCta href="/sign-up">Sign Up</HeaderSignUpCta>
  );

  const headerBadge = !badgeText ? null : (
    <HeaderBadge>{badgeText}</HeaderBadge>
  );

  // When a user is logged in, the services search will be hidden behind
  // a button that the user must click to reveal. The services search will
  // then be revealed as an overlay.
  const serviceSearchToggle = !isLoggedIn ? null : (
    <HeaderSearchToggle onClick={onOpenServiceSearch} />
  );

  const serviceSearchNode = !renderServicesSearch ? null : (
    <OutsideClickable onClickOutside={onCloseServiceSearch}>
      <HeaderBlockServicesSearch
        kind={{
          disguised: isLoggedIn,
          'design-system-header': isDesignSystemHeader,
        }}
        role="search"
      >
        {serviceSearchToggle}
        {renderServicesSearch()}
      </HeaderBlockServicesSearch>
    </OutsideClickable>
  );

  return (
    <HeaderBlock
      kind={{
        'logged-in': isLoggedIn,
        'logged-out': !isLoggedIn,
        'mobile-menu-open': isMenuVisible,
      }}
      role="banner"
    >
      {/* Overlay for Services Search (visible when user is logged in) */}
      <HeaderBlockOverlay
        aria-hidden={true}
        href="#"
        kind={isServiceSearchVisible && isLoggedIn ? 'visible' : 'hidden'}
        onClick={e => {
          e.preventDefault();
          onCloseServiceSearch();
        }}
        tabIndex="-1"
      />

      <HeaderBlockContainer
        kind={{
          'search-enabled': renderServicesSearch,
          'design-system-header': isDesignSystemHeader,
        }}
      >
        <HeaderBlockInnerWrapper
          kind={{
            'full-width': isFullWidth,
            'design-system-header': isDesignSystemHeader,
          }}
        >
          {/* Header Controls - Menu button (mobile), Logo, Signup CTA */}
          <HeaderBlockMenuControls kind={{ stretch: !renderServicesSearch }}>
            <HeaderMenuButton
              iconBefore={isMenuVisible ? 'delete' : 'menu'}
              onClick={onToggleMenu}
            >
              {isMenuVisible ? 'Close' : 'Menu'}
            </HeaderMenuButton>

            <HeaderBlockLogo />

            {headerBadge}
            {signUpCta}
          </HeaderBlockMenuControls>

          {/* Services Search */}
          {serviceSearchNode}

          {/* Navigation Links */}
          <HeaderBlockHeaderLinks
            kind={{
              open: isMenuVisible,
              stretch: !renderServicesSearch,
            }}
          >
            <HeaderLinks
              ariaLabel="Main Zapier links"
              currentUrl={currentUrl}
              enableMegaMenu={!isLoggedIn}
              forceRefreshOnExit={forceRefreshOnExit}
              isMobileMenu={isMobileMenuEnabled}
              kind={{
                links: true,
                'logged-in': isLoggedIn,
                'logged-out': !isLoggedIn,
              }}
              links={links}
              onClickLink={onHideMenu}
            />

            <HeaderLinks
              ariaLabel="Account links"
              currentUrl={currentUrl}
              forceRefreshOnExit={forceRefreshOnExit}
              isMobileMenu={isMobileMenuEnabled}
              kind={{
                actions: true,
                'logged-in': isLoggedIn,
                'logged-out': !isLoggedIn,
              }}
              links={actions}
              onClickLink={onHideMenu}
            />
          </HeaderBlockHeaderLinks>

          {renderAccountActions()}
        </HeaderBlockInnerWrapper>
      </HeaderBlockContainer>
    </HeaderBlock>
  );
};

ContentHeader.defaultProps = {
  forceRefreshOnExit: false,
  isDesignSystemHeader: false,
};

export default ContentHeader;
